import type { MutableRefObject } from "react";
import { useGridSelector, gridColumnVisibilityModelSelector } from "@mui/x-data-grid-pro";
import type { GridApi, GridColumnVisibilityModel } from "@mui/x-data-grid-pro";

/**
 * Returns the count of columns hidden by the user relative to a table's default visibility,
 * live-updating as the grid's visibility model changes. Columns not present in
 * `defaultVisibilityModel` (e.g. columns hidden by business logic rather than user action)
 * are never counted.
 *
 * `defaultVisibilityModel` must be a stable, code-defined constant representing the table's
 * shipped default (e.g. `CITATION_DEFAULT_VISIBILITY`) — never derive it from `DataTableContainer`'s
 * restored `initialState`/localStorage value. This hook already correctly reflects a hidden column
 * across a page reload (it compares the grid's *live* model, which DataGridPro seeds from
 * `initialState` on mount, against this fixed default) — but if `defaultVisibilityModel` itself were
 * derived from the restored state instead, the comparison baseline would silently shift to match
 * whatever was last saved, permanently erasing the indicator.
 */
export function useHiddenColumnsCount(
  apiRef: MutableRefObject<GridApi>,
  defaultVisibilityModel: GridColumnVisibilityModel,
): number {
  const columnVisibilityModel = useGridSelector(apiRef, gridColumnVisibilityModelSelector);

  return Object.keys(defaultVisibilityModel).filter(
    (field) => defaultVisibilityModel[field] === true && columnVisibilityModel[field] === false,
  ).length;
}
